import React from "react";
import ReactDOM from "react-dom/client";
import App from "./app/app.tsx";
import "./app/index.css";
import "react-toastify/dist/ReactToastify.css";
import "react-tooltip/dist/react-tooltip.css";
import { SessionContextProvider } from "@supabase/auth-helpers-react";
import { supabase } from "./common/lib/supabaseClient.ts";
import { DataContextProvider } from "./state/providers/DataProvider.tsx";
import { IntercomManager } from "./common/lib/Intercom.tsx";
import { RealTimeContextProvider } from "./state/providers/RealtimeProvider.tsx";
import { ToastContainer } from "react-toastify";
import * as Sentry from "@sentry/react";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router";
import store from "./app/store.ts";
import { Provider } from "react-redux";
import { AccountProvider } from "@providers/AccountProvider.tsx";
import { LockProvider } from "@/state/providers/LockProvider";
import LockScreenOverlay from "@/features/lock-screen/LockScreenOverlay";
import {
    VITE_GOOGLE_RECAPTCHA_SITE_KEY,
    VITE_GTM_AUTH,
    VITE_GTM_ID,
    VITE_GTM_PREVIEW,
    VITE_PUBLIC_ENV,
} from "@common/utils/constants";
import { IntegrationProvider } from "./state/providers/IntegrationProvider.tsx";
import { ImpersonationProvider } from "./state/providers/ImpersonationProvider.tsx";
import { ChromeExtensionProvider } from "./state/providers/ChromeExtensionProvider.tsx";
import { ThemeProvider } from "@mui/material/styles";
import { MUITheme } from "./theme";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { buildBeforeSend, buildTracesSampler } from "@common/lib/sentryUtils";
import { DATA_LAYER_NAME } from "@common/utils/dataLayer";

// Create a QueryClient instance
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 2 * 60 * 1000, // 2 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes
            // Don't refetch on window focus — prevents a thundering herd of simultaneous
            // background refetches that all fail together when a user returns to the tab
            // on an unstable connection, generating a wave of "Failed to fetch" in Sentry.
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
                // Don't retry aborted requests (component unmounted mid-flight)
                if (
                    (error as any)?.name === "AbortError" ||
                    (error as any)?.message?.toLowerCase().includes("aborted")
                ) {
                    return false;
                }
                return failureCount < 3;
            },
        },
    },
});

// check if the environment is live (production or preview)
const isLiveEnv = VITE_PUBLIC_ENV && (VITE_PUBLIC_ENV === "production" || VITE_PUBLIC_ENV === "preview");
// check if the environment is production (only production should send events to Sentry)
const isProductionEnv = VITE_PUBLIC_ENV === "production";

const errorCooldowns = new Map(); // Store user error timestamps

function loadGoogleTagManager() {
    if (!isLiveEnv || !VITE_GTM_ID) {
        return;
    }

    const dataLayerName = DATA_LAYER_NAME;
    const win = window as any;

    win[dataLayerName] = win[dataLayerName] || [];
    win[dataLayerName].push({
        "gtm.blocklist": ["customScripts", "customPixels", "html", "nonGoogleScripts"],
    });
    win[dataLayerName].push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
    });

    const firstScript = document.getElementsByTagName("script")[0];
    const script = document.createElement("script");
    const searchParams = new URLSearchParams({ id: VITE_GTM_ID });
    searchParams.set("l", dataLayerName);
    if (VITE_GTM_AUTH && VITE_GTM_PREVIEW) {
        searchParams.set("gtm_auth", VITE_GTM_AUTH);
        searchParams.set("gtm_preview", VITE_GTM_PREVIEW);
        searchParams.set("gtm_cookies_win", "x");
    }

    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?${searchParams.toString()}`;
    if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    } else {
        document.head.appendChild(script);
    }
}

function loadRecaptcha() {
    if (!VITE_GOOGLE_RECAPTCHA_SITE_KEY) {
        return;
    }

    const script = document.createElement("script");
    const searchParams = new URLSearchParams({ render: VITE_GOOGLE_RECAPTCHA_SITE_KEY });
    script.src = `https://www.google.com/recaptcha/api.js?${searchParams.toString()}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
}

loadGoogleTagManager();
loadRecaptcha();

// Track page unload so we can drop "Failed to fetch" errors caused by window.location.reload()
// (e.g. CacheBuster forcing a page refresh on version change cancels all in-flight requests).
let isPageUnloading = false;
window.addEventListener("beforeunload", () => {
    isPageUnloading = true;
});
// Reset the flag if navigation is cancelled (user clicks "Stay on page")
window.addEventListener("focus", () => {
    isPageUnloading = false;
});
// ignore these errors from being sent to Sentry
const ignoreReplayErrors = ["Object captured as exception with keys: code, details, hint, message"];

/**
 * Gets the app version from localStorage
 * @returns The app version number as a string, or "unknown" if not available
 */
function getAppVersionFromStorage(): string {
    try {
        const appVersions = localStorage.getItem("app_versions");
        if (appVersions) {
            const parsed = JSON.parse(appVersions);
            return parsed.version?.toString() || "unknown";
        }
    } catch {
        // If there's an error parsing, return unknown
    }
    return "unknown";
}

/**
 * Determines whether an error message should be sent based on the user ID and error message.
 * Implements a cool-down mechanism to prevent sending the same error message for the same user
 * within a 10-minute window.
 *
 * @param userId - The ID of the user. Can be a string, number, or undefined.
 * @param errorMessage - The error message to be sent. Can be a string or undefined.
 * @returns `true` if the error message should be sent, `false` otherwise.
 */
function shouldSendError(userId: string | number | undefined, errorMessage: string | undefined) {
    // if either the user ID or error message is missing, don't send the error
    if (!userId || !errorMessage) {
        return false;
    }

    const key = `${userId}:${errorMessage}`;
    const lastErrorTime = errorCooldowns.get(key);
    const now = Date.now();

    // error happened in the last 10 minutes
    if (lastErrorTime && now - lastErrorTime < 600000) {
        // 10 minutes
        return false;
    }

    errorCooldowns.set(key, now);
    return true;
}

try {
    const appVersion = getAppVersionFromStorage();
    Sentry.init({
        dsn: "https://6a322cf643e49d5bd1d053b710a8aae5@o4506666677239808.ingest.sentry.io/4506667695931392",
        // replaysSessionSampleRate: isLiveEnv ? sentrySessionSampleRate : 0,
        // replaysOnErrorSampleRate: isLiveEnv ? 1.0 : 0, // 100% of sessions
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,

        // Because of an issue with MUI (possibly introduced with v7), DataGrids are triggering an error
        // that the height is zero when being used in Headless UI Dialogs. This appears to be because
        // the height is set to zero when the Dialog is closed, but when when the dialog is opened, the
        // error is triggered before it can recalculate the height. The only place where this is
        // happening is the LocalStorageModal. If it fixes itself, we can remove this.
        ignoreErrors: [
            "useResizeContainer - The parent DOM element of the Data Grid has an empty height.",
            // AbortErrors are expected cancellations (navigation, component unmount, superseded requests).
            "AbortError",
        ],
        //debug: true,
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                beforeErrorSampling: (error) => {
                    // if the error message is in the ignoreReplayErrors array, return false (don't send the replay)
                    if (error.message && ignoreReplayErrors.includes(error?.message)) {
                        return false;
                    }
                    return true;
                },
            }),
            Sentry.captureConsoleIntegration({ levels: ["error"] }),
        ],
        environment: VITE_PUBLIC_ENV,

        // Per-environment transaction sampling (production 10%), honoring parent decisions (EN-1326).
        tracesSampler: buildTracesSampler(VITE_PUBLIC_ENV),

        // Trim contextual data after 5 levels of depth
        normalizeDepth: 5,

        // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: isLiveEnv
            ? [
                  /^https:\/\/service.gohappydoc.com\/api-server/,
                  /^https:\/\/service.gohappydoc.com\/service-transcribe/,
                  /^https:\/\/kezqvoudwepwufpjcjkk.supabase.co/,
                  /^https:\/\/supa.happydoc.ai/,
              ]
            : [
                  "localhost",
                  /^https:\/\/test.gohappydoc.com\/api-server/,
                  /^https:\/\/test.gohappydoc.com\/service-transcribe/,
                  /^https:\/\/ntelvrlfzbgggnlgnzup.supabase.co/,
              ],
        beforeSend: buildBeforeSend({
            isProductionEnv,
            getIsPageUnloading: () => isPageUnloading,
            appVersion,
            shouldSendError,
        }),
        beforeBreadcrumb: (breadcrumb) => {
            // log to console if env === development
            if (process.env.VITE_PUBLIC_ENV === "development") {
                console.log(breadcrumb);
            }

            return breadcrumb;
        },
        enabled: isProductionEnv,
    });

    // Set initial app_version tag
    const initialAppVersion = getAppVersionFromStorage();
    Sentry.setTag("app_version", initialAppVersion);
} catch (e) {
    console.error("Error initializing Sentry", e);
}

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <Provider store={store}>
            <QueryClientProvider client={queryClient}>
                <SessionContextProvider supabaseClient={supabase}>
                    <IntercomManager />
                    <ImpersonationProvider>
                        <AccountProvider>
                            <LockProvider>
                                <LockScreenOverlay />
                                <DataContextProvider>
                                    <IntegrationProvider>
                                        <RealTimeContextProvider>
                                            <ChromeExtensionProvider>
                                                <ThemeProvider theme={MUITheme}>
                                                    <div className="min-h-screen bg-gray-100 text-gray-900">
                                                        <App />
                                                    </div>
                                                </ThemeProvider>
                                            </ChromeExtensionProvider>
                                        </RealTimeContextProvider>
                                    </IntegrationProvider>
                                </DataContextProvider>
                            </LockProvider>
                        </AccountProvider>
                    </ImpersonationProvider>
                    <ToastContainer />
                </SessionContextProvider>
            </QueryClientProvider>
        </Provider>
    </React.StrictMode>,
);
